import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to fetch data from an API.
 *
 * @param {Promise} apiCall - The API call function to fetch data.
 * @param {...any} args - Additional arguments to pass to the API call.
 * @returns {{ data: any, loading: boolean, isError: boolean, error: any, refetch: () => Promise<any> }} An object containing:
 *  - data: The fetched data.
 *  - loading: Boolean indicating if the data is being loaded.
 *  - isError: Boolean indicating if there was an error during fetch.
 *  - error: The error object if an error occurred.
 *  - refetch: Function to refetch the data.
 */

const useFetchData = (apiCall, ...args) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiCall(...args);
      setData({ ...result });
    } catch (err) {
      setIsError(true);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [...args]);

  useEffect(() => {
    fetchData();
    return () => {
      setLoading(false);
      setIsError(false);
      setError(null);
    };
  }, [fetchData]);

  return { data, loading, isError, error, refetch: fetchData };
};

export default useFetchData;
